// CHAT-PANEL (founder review 2026-10-28): the side panel's width, dragged from its left edge.
// Kept per user as a FRACTION of the window's width (not px), so the same view looks the same on
// any screen: the px width is worked out again from the fraction on load and on every window
// resize. At least 360 px (the panel's width before it could be resized); at most a third of the
// window, or 360 px when a third of a small window is less.
import { useCallback, useEffect, useState } from "react";

/** The side panel's narrowest width (the design token `--panel-w`). */
export const PANEL_MIN_PX = 360;
/** The panel never takes more than this share of the window. */
export const PANEL_MAX_SHARE = 1 / 3;

const KEY = (userId: string) => `companyos.side-panel.width.${userId}`;

/** The widest the panel may be in a window `viewport` px wide. */
export function panelMaxPx(viewport: number): number {
  return Math.max(PANEL_MIN_PX, Math.floor(viewport * PANEL_MAX_SHARE));
}

/** `px` held between the minimum and a third of the window. */
export function clampPanelPx(px: number, viewport: number): number {
  if (!Number.isFinite(px)) return PANEL_MIN_PX;
  return Math.round(Math.min(panelMaxPx(viewport), Math.max(PANEL_MIN_PX, px)));
}

/** The px width a saved fraction gives in a window `viewport` px wide (null: the minimum). */
export function widthFromFraction(fraction: number | null, viewport: number): number {
  if (fraction === null || !Number.isFinite(fraction) || fraction <= 0) return PANEL_MIN_PX;
  return clampPanelPx(fraction * viewport, viewport);
}

/** The saved fraction for this person, or null (none saved, unreadable, or storage blocked). */
export function readPanelFraction(userId: string): number | null {
  try {
    const raw = window.localStorage.getItem(KEY(userId));
    if (raw === null) return null;
    const v = Number(raw);
    return Number.isFinite(v) && v > 0 && v <= 1 ? v : null;
  } catch {
    return null;
  }
}

/** Saves the fraction (null forgets it: the default width). */
export function writePanelFraction(userId: string, fraction: number | null): void {
  try {
    if (fraction === null) window.localStorage.removeItem(KEY(userId));
    else window.localStorage.setItem(KEY(userId), String(Number(fraction.toFixed(4))));
  } catch {
    // storage blocked: the width holds for this page
  }
}

const viewportNow = () => (typeof window === "undefined" ? 1440 : window.innerWidth);

/**
 * The panel's live width for this person: from the saved fraction, recomputed on window resize.
 * `resize(px, done)` follows a drag (saved once `done`); `reset()` forgets the saved width.
 */
export function usePanelWidth(userId: string) {
  const [fraction, setFraction] = useState(() => readPanelFraction(userId));
  const [viewport, setViewport] = useState(viewportNow);
  // Another person signing in on this tab reads their own width.
  useEffect(() => setFraction(readPanelFraction(userId)), [userId]);
  useEffect(() => {
    const onResize = () => setViewport(viewportNow());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const width = widthFromFraction(fraction, viewport);
  const resize = useCallback(
    (px: number, done: boolean) => {
      const vw = viewportNow();
      const next = clampPanelPx(px, vw) / vw;
      setViewport(vw);
      setFraction(next);
      if (done) writePanelFraction(userId, next);
    },
    [userId],
  );
  const reset = useCallback(() => {
    setFraction(null);
    writePanelFraction(userId, null);
  }, [userId]);
  return { width, min: PANEL_MIN_PX, max: panelMaxPx(viewport), resize, reset };
}
